import { useCallback, useEffect, useRef } from "react";
import { useMutation } from "@apollo/client/react";
import { useIntl } from "react-intl";
import {
  AddCompanyMemberDocument,
  CompanyCompetenciesDocument,
  CompanyContactPersonsDocument,
  CompanyMembersDocument,
  CompanyProfileDocument,
  RemoveCompanyCompetencyDocument,
  RemoveCompanyMemberDocument,
  SetCompanyCompetencyDocument,
  SetCompanyContactPersonDocument,
  SetCompanyMemberRoleDocument,
  UpdateCompanyDocument,
} from "@/graphql/generated/operations";
import { CompanyStatus } from "@/graphql/generated/types";
import { MAX_COMPANY_LOGO_BYTES, MB } from "@/lib/upload-limits";
import {
  companyAdminErrorMessage,
  companyValidationMessage,
  type CompanyRefusals,
} from "./company-admin-error";
import type { CompanyRole } from "./company-roles";
import { MEMBER_PAGE_SIZE } from "./use-company";
import {
  COMPANY_EXPERTISE_ALREADY_LISTED,
  COMPANY_EXPERTISE_LABEL_TOO_LONG,
  COMPANY_EXPERTISE_LIST_FULL,
  LAST_COMPANY_ADMIN,
  PERSON_CANNOT_ADMINISTER_COMPANY,
  TOO_MANY_CONTACT_PERSONS,
} from "@/lib/genesis-error-codes";

// The write half of /c/:id, for an admin of THIS company (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization): edit the profile, manage
// the expertise, manage the members.
//
// NOT everything §6 permits — publishing in the company's name is the fourth thing it allows since
// concept v7, and it is not here. It belongs to the composer, which sends `company` on
// `createSharedItem` (ShareCreatePage), because it writes a shared item rather than the company.
// Matching as a company stays out (v3 deferred it), and there is still no company feed.
//
// NO ACTING-COMPANY STATE REACHES THIS FILE, AND NONE MAY. spark does now have a company context
// (stores/company-context-store.ts, the company account model), and this
// module deliberately does not import it. Every mutation carries the company id of the page it was
// invoked on as its TARGET, and genesis re-checks the `company_membership` row for that target on
// each one (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization). A write that took its company from ambient browser state would be a
// write authorized by the browser, on a transport whose proxy does no authorization at all.
//
// Every action ANSWERS its failure instead of throwing it. The refusals genesis makes on purpose —
// the last admin, a duplicate member — are the whole reason an admin can act next, so each call
// site passes the one refusal its operation can produce and gets it back already localized.

// The role vocabulary lives in ./company-roles, which is also what the read side and the
// acting-company bar compare against. Re-exported nowhere: callers import it from there.

/**
 * How many members a company may mark as contact persons.
 *
 * A COPY of genesis's CompanyMembershipController.MAX_CONTACT_PERSONS, held here so the editor can
 * disable the control before the click rather than only explaining the refusal after it. genesis is
 * the authority and enforces the same number under a row lock on the company; if the two ever
 * disagree, this one is wrong and the server still wins — which is why the refusal path exists as
 * well and is not treated as unreachable. IT HAS TO MOVE IN THE SAME RELEASE AS GENESIS'S, or the
 * form refuses at one number while the server accepts another.
 *
 * Ten since genesis#268. The concept wrote "bis zu drei" until concept v13 amended
 * the company page to match the code that had already shipped.
 * Three was not a curation but an omission — sales, HR and a technical
 * lead fill it, and the next real contact could only be added by removing one. It stays capped
 * because the block is a shortlist and a company that marks fifty people has published the directory
 * the member roster already is.
 */
export const MAX_CONTACT_PERSONS = 10;

/**
 * How long an expertise label may be, trimmed — genesis's `company_competency.label varchar(255)`
 * and the `label must be at most 255 characters long` check in front of it.
 *
 * A COPY, held here for the same reason as MAX_CONTACT_PERSONS: the editor can refuse before the
 * click instead of only explaining afterwards. genesis is the authority, and the refusal path
 * (GENESIS001229) exists as well rather than being treated as unreachable, so the two drifting
 * apart costs a round trip and an actionable sentence rather than a generic failure.
 */
export const MAX_COMPETENCY_LABEL_LENGTH = 255;

/**
 * How many expertise entries one company may list — genesis's
 * `CompanyCompetencyController.MAX_COMPETENCIES`.
 *
 * A COPY, for the same reason as the two caps above: the editor disables Add before the click rather
 * than only explaining the refusal after it. genesis is the authority and enforces the same number
 * under a row lock on the company row, so the refusal path (GENESIS001249) stays live — it is what
 * answers when this copy is out of date, and when a second admin fills the last slot between this
 * page's read and this admin's click.
 *
 * The number is not cosmetic. genesis folds every one of a company's labels into that company's ONE
 * `global_search` tsvector, and PostgreSQL refuses a tsvector past 1 MB; an unbounded list lets one
 * company reach a state where reindexing it throws, which then aborts a platform admin's industry
 * rename for every other company in that industry. Lowering this copy is safe; raising it past
 * genesis's own number only means the server refuses instead of the button.
 */
export const MAX_COMPETENCY_COUNT = 100;

/**
 * "This would be one contact person past the cap." Matched exactly rather than by classification: this
 * mutation can reach more than one refusal, so the loose match would let a future one inherit the
 * cap's wording.
 */
const CODE_TOO_MANY_CONTACT_PERSONS = TOO_MANY_CONTACT_PERSONS;

/**
 * genesis's catalogued code for "this would leave the company with no usable administrator"
 * — see @/lib/genesis-error-codes.
 *
 * Matched by CODE now that genesis catalogues it. It used to be recognised by classification alone,
 * on the reasoning that it was the only VALIDATION these mutations could reach — which stopped
 * being true the moment GENESIS001242 appeared on the same two operations.
 */
const CODE_LAST_ADMIN = LAST_COMPANY_ADMIN;

/**
 * genesis's catalogued code for "that person's account is not active, so they cannot administer a
 * company" (the ACCOUNT half of the orphaning guard — the
 * identity half was built and reverted, so the strand is still open on genesis's side).
 *
 * MUST stay distinguishable from GENESIS001240: the two ask for opposite repairs. 001240 says
 * promote somebody else before you leave; 001242 says the person you just picked cannot be promoted
 * at all. Answering one with the other's wording sends the admin round in a circle.
 */
const CODE_CANNOT_ADMINISTER = PERSON_CANNOT_ADMINISTER_COMPANY;

export type AdminResult = { ok: true } | { ok: false; message: string };

export interface CompanyProfileInput {
  name: string;
  industryId: string | null;
  employeeCount: number | null;
  summary: string | null;
}

/** genesis's CreateFileDataInput: `content` is the Binary scalar, i.e. base64 of the raw bytes. */
export interface CompanyLogoInput {
  fileName: string;
  mimeType: string;
  content: string;
}

/**
 * WHERE THE COMPANY COMES FROM WHEN IT DOES NOT EXIST YET (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2).
 *
 * On `/c/:id` every write already knows its target. On the create state of the same page the id is
 * the one thing nobody has: genesis mints it, and a competency, a membership or a contact-person
 * mark can only point at a row that is already there. So the create state hands this hook a gate
 * instead of an id — one LATCHED promise that creates the company on its first call and answers the
 * same id to every caller after that.
 *
 * The latch is the whole point, and it is not the same as the create page's old double-click guard
 * (which cleared itself in a `finally` and would happily create a second company on a second Save).
 * Both flush drivers — the SaveBar and the unsaved-changes dialog — fan out over the scopes with
 * `Promise.all`, so several writes ask for the id in the same tick and every one of them must get
 * the company the first of them created.
 */
export type CompanyCreateGate = () => Promise<
  { ok: true; id: string } | { ok: false; message: string }
>;

export interface UseCompanyAdminResult {
  saveProfile: (input: CompanyProfileInput) => Promise<AdminResult>;
  /**
   * Deactivates or reactivates the company (spark#253). Writes IMMEDIATELY — this is the one company
   * edit that does not go through the SaveBar draft, because the company page's v7 change note calls the
   * status a lifecycle state rather than a profile value. Carries the STORED name, never a draft's.
   */
  setStatus: (status: CompanyStatus) => Promise<AdminResult>;
  /** Replaces the logo. Leaves every other company field untouched. */
  saveLogo: (logo: CompanyLogoInput) => Promise<AdminResult>;
  /** Clears the logo, so the page falls back to the monogram. */
  removeLogo: () => Promise<AdminResult>;
  /**
   * ADDS one expertise to this company. Despite genesis's mutation name this is not an upsert: a
   * label the company already lists is refused (GENESIS001247), and changing a label is a
   * {@link removeCompetency} followed by a fresh add. Since concept v17 a label is all an entry has
   * — the level is gone on both sides.
   *
   * @param label free text owned by this company. Sent TRIMMED, because genesis stores it trimmed
   *   and compares it trimmed, so an untrimmed value would be quoted back with the stray space in
   *   the admin's own refusal message.
   */
  setCompetency: (label: string) => Promise<AdminResult>;
  /** Removes one expertise, matched on its label case- and whitespace-insensitively. */
  removeCompetency: (label: string) => Promise<AdminResult>;
  addMember: (personId: string, role: CompanyRole) => Promise<AdminResult>;
  setMemberRole: (personId: string, role: CompanyRole) => Promise<AdminResult>;
  removeMember: (personId: string) => Promise<AdminResult>;
  /**
   * Marks or unmarks an EXISTING member as a contact person (the company page). Orthogonal to the role:
   * this never changes it, and a marked member stays marked across promotion and demotion.
   */
  setContactPerson: (
    personId: string,
    contactPerson: boolean,
  ) => Promise<AdminResult>;
  busy: boolean;
}

const profileRefetch = (companyId: string) => [
  { query: CompanyProfileDocument, variables: { input: { id: companyId } } },
];
const competencyRefetch = (companyId: string) => [
  { query: CompanyCompetenciesDocument, variables: { input: { companyId } } },
];
// BOTH member reads, and the variables have to be the ones the page actually mounted. A
// refetchQueries entry is matched on query + variables, so `{ companyId }` alone would no longer
// name the paged query the strip is watching — it would issue a second, unwatched request and
// leave the strip showing the roster from before the write.
//
// The contact persons are the second entry because they stopped being a projection of the first
// (@external-adr GENESIS-0029-the-member-strip-is-paged-and-the-contact-strip-is-not): marking somebody now changes a list this one does not contain, so a write
// that refetched only the roster would leave the contact block stating the previous answer.
// Deliberately back to page one — see the note on `refetch` in use-company.
const memberRefetch = (companyId: string) => [
  {
    query: CompanyMembersDocument,
    variables: { input: { companyId, first: MEMBER_PAGE_SIZE } },
  },
  { query: CompanyContactPersonsDocument, variables: { input: { companyId } } },
];
// THE TWO WRITES THAT CAN CHANGE THE CALLER'S OWN ROW also have to re-read what the caller is.
// Demoting yourself to `member` or removing yourself is an ordinary use of these buttons, and both
// reads above are about OTHER people — so without this the admin stayed in the company context and
// kept the page's admin affordances until a reload, acting as a company they no longer administer
// (concept v10, 0002 §5).
//
// BY NAME, not by document. A name entry refetches the query only where it is currently mounted:
// `MyCompanies` is skipped unless a context is actually active, and `MyCompanyRoles` unless a
// company page is open, so a role change made from somewhere else costs nothing. Passing the
// documents instead would issue both requests unconditionally.
//
// `useCompanyContext` does the dropping itself once the answer lands — a membership that no longer
// resolves to an admin row clears the store — so this list only has to make the answer arrive.
const ownMembershipRefetch = (companyId: string) => [
  ...memberRefetch(companyId),
  "MyCompanies",
  "MyCompanyRoles",
];

/**
 * @param companyName the company's STORED name. `UpdateCompanyDataInput.name` is `String!`, so even
 *   a write that only touches the logo has to carry it; sending the stored one leaves it unchanged.
 *   Deliberately not the profile draft — a logo write must never commit a name edit the admin has
 *   not saved. The two are separate pending scopes precisely so the save bar can save either on its
 *   own; neither has a save button of its own any more (jfalkenstein, !238 note_25750).
 *
 * @param ensureCompany present only in the create state (@adr SPARK-0038-creating-a-company-is-the-company-page-before-it-exists@2). When it is, `companyId` is empty
 *   until the gate has answered, so EVERY write resolves its target through the gate at call time
 *   rather than reading the id this render happened to close over — and so does the refetch each one
 *   asks for, which is why the refetch sets below are built per call instead of once per render.
 */
export function useCompanyAdmin(
  companyId: string,
  companyName: string,
  ensureCompany?: CompanyCreateGate,
): UseCompanyAdminResult {
  const intl = useIntl();

  // Read at CALL time, never closed over. The create gate answers with an id that did not exist when
  // any of these callbacks was built, and the callbacks below run in that same tick.
  //
  // Kept current from an effect rather than during render, which the React compiler forbids and
  // which nothing here needs: every reader is a user-initiated write, and React flushes passive
  // effects before it processes the next event.
  const idRef = useRef(companyId);
  const nameRef = useRef(companyName);
  const gateRef = useRef(ensureCompany);
  useEffect(() => {
    idRef.current = companyId;
    nameRef.current = companyName;
    gateRef.current = ensureCompany;
  }, [companyId, companyName, ensureCompany]);

  /**
   * Whether the gate has ever produced an id. NOT the same question as "is a gate present": the gate
   * stays in place until the created company has been read back, so `gateRef.current` is still set
   * for a while after the company exists — and during that window a profile or logo write is a
   * genuine update, not part of the create.
   */
  const created = useRef(false);

  /**
   * The company every write below addresses — created first if it does not exist yet.
   *
   * `fresh` says whether THIS resolution is the one that brought the company into being. Only the
   * two writes the create itself carries — the profile and the logo — care, and only so they can
   * skip re-sending what genesis stored a moment ago. Everything else is an ordinary write against
   * an id that now exists.
   */
  const resolveTarget = useCallback(async (): Promise<
    { ok: true; id: string; fresh: boolean } | { ok: false; message: string }
  > => {
    const gate = gateRef.current;
    if (!gate) return { ok: true, id: idRef.current, fresh: false };
    const before = created.current;
    const result = await gate();
    if (!result.ok) return result;
    created.current = true;
    return { ok: true, id: result.id, fresh: !before };
  }, []);

  const [updateCompany, updateState] = useMutation(UpdateCompanyDocument);
  const [setCompetencyMut, setCompetencyState] = useMutation(
    SetCompanyCompetencyDocument,
  );
  const [removeCompetencyMut, removeCompetencyState] = useMutation(
    RemoveCompanyCompetencyDocument,
  );
  const [addMemberMut, addMemberState] = useMutation(AddCompanyMemberDocument);
  const [setRoleMut, setRoleState] = useMutation(SetCompanyMemberRoleDocument);
  const [removeMemberMut, removeMemberState] = useMutation(
    RemoveCompanyMemberDocument,
  );
  // Re-reads BOTH member queries, which is what `memberRefetch` now means and why this write in
  // particular needs it: the mark it writes decides membership of the contact list, and shows up on
  // the roster as each row's own `contactPerson` flag. Refetching one would leave the other stating
  // the answer from before the click.
  const [setContactPersonMut, setContactPersonState] = useMutation(
    SetCompanyContactPersonDocument,
  );

  const lastAdmin = {
    code: CODE_LAST_ADMIN,
    message: intl.formatMessage({
      id: "company.admin.error.lastAdmin",
      defaultMessage:
        "A company must keep at least one administrator. Make somebody else an administrator first.",
    }),
  };
  // "Account is not active" is what genesis actually refuses on: PersonFilterSpec.activePerson, i.e.
  // suspended or soft-deleted. It does NOT mean "has never signed in" — that rule was reverted with
  // person.has_authenticated (@external-adr GENESIS-0023-company-membership-is-the-per-company-authorization §8), and genesis can no longer tell the two apart. The
  // wording must not send the admin looking for an account that exists and is merely suspended.
  const cannotAdminister = {
    code: CODE_CANNOT_ADMINISTER,
    message: intl.formatMessage({
      id: "company.admin.error.cannotAdminister",
      defaultMessage:
        "This person's account is not active, so they cannot administer a company. Choose somebody whose account is still active.",
    }),
  };
  const alreadyMember = intl.formatMessage({
    id: "company.admin.error.alreadyMember",
    defaultMessage: "This person is already listed at this company.",
  });
  // The cap is checked before the click too, so reaching this means the count spark held was stale —
  // somebody else took the last free slot in another session. Worded as the rule rather than
  // as a failure, because the admin's next move is to unmark somebody, not to retry.
  //
  // Kept as a plain string, not the {code, message} pair the mapper takes: a fresh object literal
  // every render would make the callback below a new function every render for no reason. The pair
  // is built at the call site, where it is only reached on failure.
  const tooManyContactPersons = intl.formatMessage(
    {
      id: "company.admin.error.tooManyContactPersons",
      defaultMessage:
        "A company can have at most {max, number} contact persons. Somebody else has just used the last slot, so unmark one to mark this person.",
    },
    { max: MAX_CONTACT_PERSONS },
  );
  // genesis is the authority on the logo and refuses two ways — over the cap, or bytes that are not
  // one of the raster formats it accepts. Both arrive as an InputDataValidationException carrying no
  // code and an English message quoting raw byte counts, so spark cannot tell them apart and must
  // not repeat what genesis said (@adr SPARK-0010-no-raw-server-errors-in-ui@1). One message that names both rules is the honest answer,
  // and this is the ONLY refusal a logo write can reach: the request changes nothing else.
  const logoRefused = intl.formatMessage(
    {
      id: "company.admin.error.logoRefused",
      defaultMessage:
        "The logo was refused. It has to be a PNG, JPEG, GIF, BMP, TIFF or WebP image of at most {max, number} MB.",
    },
    { max: Math.floor(MAX_COMPANY_LOGO_BYTES / MB) },
  );

  const failure = useCallback(
    (error: unknown, refusal: CompanyRefusals | null): AdminResult => ({
      ok: false,
      message: companyAdminErrorMessage(intl, error, refusal),
    }),
    [intl],
  );

  /**
   * Deactivates or reactivates the company — the whole of spark#253.
   *
   * <p>ONE `updateCompany`, not a mutation of its own. `Company.status` is `updatable: true` and
   * `updateCompany` is already gated on an admin `company_membership` row, which is exactly the actor
   * the company entity model (v7) names: an admin of THIS company, never platform moderation. So
   * genesis needed nothing for this.
   *
   * <p>NOT PART OF THE PROFILE DRAFT, deliberately. Every other company edit is staged and written by
   * the SaveBar, and this one is not, because 0006's v7 change note says the status is "kein
   * Steckbriefwert, sondern ein Lebenszyklus-Zustand" — the draft is the Steckbrief mechanism. (Not
   * §4, which is the contact-person cap.) Staging it would also
   * put "deactivate" and "rename" behind one Save, so an admin who wanted the rename would have to
   * take the deactivation with it.
   *
   * <p>IT HAS TO CARRY THE NAME. `UpdateCompanyDataInput.name` is `String!` — the generator keeps a
   * non-null scalar non-null on the update input — so there is no such thing as a status-only write
   * on this mutation. It rides on `nameRef`, the STORED name this hook already holds for exactly
   * this reason (see the parameter's doc), never on anything a draft is currently typing: re-sending
   * the stored value is a no-op against the row, whereas re-sending an unsaved draft would smuggle a
   * rename into a deactivation. Every other field is simply absent, and genesis leaves an
   * unmentioned property alone.
   *
   * <p>Deactivating is REVERSIBLE and empties nothing (0001 §1, v7): name, logo, expertise, members
   * and contacts all stay, so reactivating is a pure status flip that restores the company whole.
   * That is why only one direction asks for a confirmation, and it is the caller that does the
   * asking — this function performs whichever act it is told to.
   */
  const setStatus = useCallback(
    async (status: CompanyStatus): Promise<AdminResult> => {
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      try {
        const response = await updateCompany({
          refetchQueries: profileRefetch(target.id),
          variables: {
            id: target.id,
            input: { data: { name: nameRef.current, status }, dryRun: false },
          },
        });
        const result = response.data?.updateCompany;
        if (result?.__typename === "ValidateCompanyResult") {
          return {
            ok: false,
            message: companyValidationMessage(
              intl,
              result,
              genericSaveFailure(intl),
            ),
          };
        }
        return { ok: true };
      } catch (error) {
        return failure(error, null);
      }
    },
    [failure, intl, resolveTarget, updateCompany],
  );

  const saveProfile = useCallback(
    async (input: CompanyProfileInput): Promise<AdminResult> => {
      // THE CREATE ITSELF carried name, industry, size, "About us" and the logo, so there is nothing
      // left for an update to say — asking for one here would be a second write of values genesis
      // stored a moment ago. Only for the resolution that CREATED the company, though: gated on the
      // gate's mere presence, a later edit made before the company had been read back would answer
      // "saved" and write nothing.
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      if (target.fresh) return { ok: true };
      try {
        const response = await updateCompany({
          refetchQueries: profileRefetch(target.id),
          variables: {
            id: target.id,
            input: {
              data: {
                name: input.name,
                // Explicit nulls, not omissions: genesis reads the update off the properties the
                // request carried, so a field left out keeps its stored value. Clearing the
                // industry or the "About us" text has to travel as a present null.
                industry: input.industryId ? { id: input.industryId } : null,
                employeeCount: input.employeeCount,
                summary: input.summary,
                // `logo` is deliberately ABSENT, not null. Absent means "leave it alone"; null
                // means "delete it". The logo has its own editor and its own write (writeLogo
                // below), so a profile save must never mention it.
              },
              dryRun: false,
            },
          },
        });
        const result = response.data?.updateCompany;
        if (result?.__typename === "ValidateCompanyResult") {
          return {
            ok: false,
            message: companyValidationMessage(
              intl,
              result,
              genericSaveFailure(intl),
            ),
          };
        }
        return { ok: true };
      } catch (error) {
        return failure(error, null);
      }
    },
    [failure, intl, resolveTarget, updateCompany],
  );

  /**
   * The logo half of updateCompany, and the ONLY place spark mentions the property.
   *
   * `logo` is present in the payload either way: an object replaces the stored logo, an explicit
   * null clears it. What is absent is everything else — industry, employeeCount and summary are not
   * mentioned at all, so genesis leaves them exactly as they are and a logo change cannot blank a
   * profile. The mirror image of saveProfile, which never mentions `logo` for the same reason.
   */
  const writeLogo = useCallback(
    async (logo: CompanyLogoInput | null): Promise<AdminResult> => {
      // Same as saveProfile: the picked logo travels inside `createCompany`, in the same transaction
      // as the company row, so there is no second request and no window in which the company exists
      // without it. And likewise only for the resolution that created it.
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      if (target.fresh) return { ok: true };
      try {
        const response = await updateCompany({
          refetchQueries: profileRefetch(target.id),
          variables: {
            id: target.id,
            input: { data: { name: nameRef.current, logo }, dryRun: false },
          },
        });
        const result = response.data?.updateCompany;
        // genesis raises the logo refusals as exceptions rather than this union branch, so this is
        // defensive. It answers with spark's own copy rather than genesis's issue text: on a request
        // that changed nothing but the logo, the logo is what was refused.
        if (result?.__typename === "ValidateCompanyResult") {
          return { ok: false, message: logoRefused };
        }
        return { ok: true };
      } catch (error) {
        return failure(error, logoRefused);
      }
    },
    [failure, logoRefused, resolveTarget, updateCompany],
  );

  const saveLogo = useCallback(
    (logo: CompanyLogoInput): Promise<AdminResult> => writeLogo(logo),
    [writeLogo],
  );

  const removeLogo = useCallback(
    (): Promise<AdminResult> => writeLogo(null),
    [writeLogo],
  );

  const setCompetency = useCallback(
    async (label: string): Promise<AdminResult> => {
      const trimmed = label.trim();
      // Built here rather than at the top of the hook: both quote the label back, so they cannot be
      // hoisted into render-stable constants the way the membership refusals are.
      const alreadyListed = {
        code: COMPANY_EXPERTISE_ALREADY_LISTED,
        message: intl.formatMessage(
          {
            id: "company.admin.error.expertiseAlreadyListed",
            defaultMessage:
              "“{label}” is already listed, so it cannot be added twice.",
          },
          { label: trimmed },
        ),
      };
      // The editor answers the same count on its own before staging, so reaching this means either
      // the two copies of the cap disagree or another admin filled the last slot since this page read
      // the list. Worded
      // as the rule rather than as a failure: the admin's next move is to remove an entry, not to
      // retry. It is listed BEFORE the duplicate below because genesis refuses in that order — at the
      // cap, re-adding a label the company already lists is answered as full, not as a duplicate.
      const listFull = {
        code: COMPANY_EXPERTISE_LIST_FULL,
        message: intl.formatMessage(
          {
            id: "company.admin.error.expertiseListFull",
            defaultMessage:
              "This company already lists {max, number} skills, which is the maximum. Remove one to add another.",
          },
          { max: MAX_COMPETENCY_COUNT },
        ),
      };
      // The editor caps the box at the same number, so reaching this means the two copies of the
      // cap disagree — genesis's is the real one.
      const tooLong = {
        code: COMPANY_EXPERTISE_LABEL_TOO_LONG,
        message: intl.formatMessage(
          {
            id: "company.admin.error.expertiseTooLong",
            defaultMessage:
              "A skill can be at most {max, number} characters long. Shorten it and add it again.",
          },
          { max: MAX_COMPETENCY_LABEL_LENGTH },
        ),
      };
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      try {
        await setCompetencyMut({
          refetchQueries: competencyRefetch(target.id),
          variables: {
            input: { companyId: target.id, label: trimmed },
          },
        });
        return { ok: true };
      } catch (error) {
        return failure(error, [listFull, alreadyListed, tooLong]);
      }
    },
    [failure, intl, resolveTarget, setCompetencyMut],
  );

  const removeCompetency = useCallback(
    async (label: string): Promise<AdminResult> => {
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      try {
        // No refusal is named: genesis answers `false` for a label this company never listed
        // instead of raising, so the only failures left are failures.
        await removeCompetencyMut({
          refetchQueries: competencyRefetch(target.id),
          variables: { input: { companyId: target.id, label: label.trim() } },
        });
        return { ok: true };
      } catch (error) {
        return failure(error, null);
      }
    },
    [failure, removeCompetencyMut, resolveTarget],
  );

  const addMember = useCallback(
    async (personId: string, role: CompanyRole): Promise<AdminResult> => {
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      try {
        await addMemberMut({
          refetchQueries: memberRefetch(target.id),
          variables: { input: { companyId: target.id, personId, role } },
        });
        return { ok: true };
      } catch (error) {
        return failure(error, [alreadyMember, cannotAdminister]);
      }
    },
    [addMemberMut, alreadyMember, cannotAdminister, failure, resolveTarget],
  );

  const setMemberRole = useCallback(
    async (personId: string, role: CompanyRole): Promise<AdminResult> => {
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      try {
        await setRoleMut({
          refetchQueries: ownMembershipRefetch(target.id),
          variables: { input: { companyId: target.id, personId, role } },
        });
        return { ok: true };
      } catch (error) {
        return failure(error, [lastAdmin, cannotAdminister]);
      }
    },
    [cannotAdminister, failure, lastAdmin, resolveTarget, setRoleMut],
  );

  const removeMember = useCallback(
    async (personId: string): Promise<AdminResult> => {
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      try {
        await removeMemberMut({
          refetchQueries: ownMembershipRefetch(target.id),
          variables: { input: { companyId: target.id, personId } },
        });
        return { ok: true };
      } catch (error) {
        return failure(error, [lastAdmin, cannotAdminister]);
      }
    },
    [cannotAdminister, failure, lastAdmin, removeMemberMut, resolveTarget],
  );

  const setContactPerson = useCallback(
    async (personId: string, contactPerson: boolean): Promise<AdminResult> => {
      const target = await resolveTarget();
      if (!target.ok) return { ok: false, message: target.message };
      try {
        await setContactPersonMut({
          refetchQueries: memberRefetch(target.id),
          variables: {
            input: { companyId: target.id, personId, contactPerson },
          },
        });
        return { ok: true };
      } catch (error) {
        // Named only on the way IN. genesis never refuses an unmark, so offering "unmark somebody
        // first" after a failed unmark would be nonsense; that direction falls through to the
        // generic failure instead.
        return failure(
          error,
          contactPerson
            ? {
                code: CODE_TOO_MANY_CONTACT_PERSONS,
                message: tooManyContactPersons,
              }
            : null,
        );
      }
    },
    [failure, resolveTarget, setContactPersonMut, tooManyContactPersons],
  );

  return {
    saveProfile,
    setStatus,
    saveLogo,
    removeLogo,
    setCompetency,
    removeCompetency,
    addMember,
    setMemberRole,
    removeMember,
    setContactPerson,
    busy:
      updateState.loading ||
      setCompetencyState.loading ||
      removeCompetencyState.loading ||
      addMemberState.loading ||
      setRoleState.loading ||
      removeMemberState.loading ||
      setContactPersonState.loading,
  };
}

function genericSaveFailure(intl: {
  formatMessage: (d: { id: string; defaultMessage: string }) => string;
}): string {
  return intl.formatMessage({
    id: "company.admin.error.saveProfile",
    defaultMessage: "The company could not be saved.",
  });
}
